import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Enhanced custom hook for debouncing a value with performance optimizations
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds
 * @param options - Additional options for debouncing behavior
 * @returns The debounced value and control functions
 */
export function useDebounce<T>(
  value: T,
  delay: number,
  options: {
    /** Enable leading edge trigger */
    leading?: boolean;
    /** Enable trailing edge trigger */
    trailing?: boolean;
    /** Maximum wait time before forcing execution */
    maxWait?: number;
  } = {}
): {
  debouncedValue: T;
  cancel: () => void;
  flush: () => void;
  isPending: () => boolean;
} {
  const { leading = false, trailing = true, maxWait } = options;

  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCallTimeRef = useRef<number>(0);
  const lastInvokeTimeRef = useRef<number>(0);
  const leadingRef = useRef<boolean>(false);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
    leadingRef.current = false;
    lastCallTimeRef.current = 0;
    lastInvokeTimeRef.current = 0;
  }, []);

  const flush = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      setDebouncedValue(value);
      lastInvokeTimeRef.current = Date.now();
    }
  }, [value]);

  const isPending = useCallback(() => {
    return timeoutRef.current !== null;
  }, []);

  const invokeFunc = useCallback(() => {
    setDebouncedValue(value);
    lastInvokeTimeRef.current = Date.now();
  }, [value]);

  const shouldInvoke = useCallback((time: number) => {
    const timeSinceLastCall = time - lastCallTimeRef.current;
    const timeSinceLastInvoke = time - lastInvokeTimeRef.current;

    return (
      lastCallTimeRef.current === 0 ||
      timeSinceLastCall >= delay ||
      timeSinceLastCall < 0 ||
      (maxWait !== undefined && timeSinceLastInvoke >= maxWait)
    );
  }, [delay, maxWait]);

  const remainingDelay = useCallback((time: number) => {
    const timeSinceLastCall = time - lastCallTimeRef.current;
    const timeSinceLastInvoke = time - lastInvokeTimeRef.current;
    const timeWaiting = delay - timeSinceLastCall;

    return maxWait === undefined
      ? timeWaiting
      : Math.min(timeWaiting, maxWait - timeSinceLastInvoke);
  }, [delay, maxWait]);

  const leadingEdge = useCallback(() => {
    lastInvokeTimeRef.current = Date.now();
    leadingRef.current = true;
    if (leading) {
      invokeFunc();
    }
  }, [leading, invokeFunc]);

  const trailingEdge = useCallback(() => {
    timeoutRef.current = null;
    if (trailing && lastCallTimeRef.current > lastInvokeTimeRef.current) {
      invokeFunc();
    }
    leadingRef.current = false;
  }, [trailing, invokeFunc]);

  const timerExpired = useCallback(() => {
    const time = Date.now();
    if (shouldInvoke(time)) {
      trailingEdge();
    } else {
      // Reschedule the timer
      const remainingWait = remainingDelay(time);
      timeoutRef.current = setTimeout(timerExpired, remainingWait);
    }
  }, [trailingEdge, shouldInvoke, remainingDelay]);

  useEffect(() => {
    const time = Date.now();
    lastCallTimeRef.current = time;

    if (shouldInvoke(time)) {
      if (timeoutRef.current === null) {
        leadingEdge();
      }
      if (maxWait !== undefined) {
        if (maxTimeoutRef.current) {
          clearTimeout(maxTimeoutRef.current);
        }
        maxTimeoutRef.current = setTimeout(invokeFunc, maxWait);
      }
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(timerExpired, delay);

    // Cleanup on unmount
    return () => {
      cancel();
    };
  }, [value, delay, shouldInvoke, leadingEdge, timerExpired, cancel, invokeFunc, maxWait]);

  return {
    debouncedValue,
    cancel,
    flush,
    isPending,
  };
}
